// src/sections/privacy/ZKPAUnlockModal.tsx
// Shown after page refresh when the user has already bootstrapped ZKPA but the
// login password is no longer in memory. Prompts for password to re-decrypt the MK.
// Options: Mở khoá (password) | Recovery (seed phrase) | Logout

import { useState } from "react";
import { SvgShieldCheck } from "@opal/icons";
import { useAuthStore } from "@/shared/stores/authStore";
import { zkpaApi } from "@/shared/api/zkpa.api";
import { MasterKeyManager } from "@/shared/lib/crypto/masterKey";
import { useZKPAStore } from "@/shared/stores/zkpaStore";
import { ZKPARecoveryModal } from "./ZKPARecoveryModal";

interface Props {
  open: boolean;
  userId: string;
  onUnlocked: () => void;
  onDismiss?: () => void;
}

export function ZKPAUnlockModal({ open, userId, onUnlocked, onDismiss }: Props) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);

  const setSessionPassword = useAuthStore((s) => s.setSessionPassword);
  const logout = useAuthStore((s) => s.logout);
  const setKeyState = useZKPAStore((s) => s.setKeyState);

  if (!open) return null;

  if (showRecovery) {
    return (
      <ZKPARecoveryModal
        open={true}
        userId={userId}
        onSuccess={() => {
          setShowRecovery(false);
          onUnlocked();
        }}
        onCancel={() => setShowRecovery(false)}
      />
    );
  }

  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    setError(null);
    try {
      const status = await zkpaApi.getEncryptedMasterKey();
      if (!status.bootstrapped) {
        setError("Không tìm thấy dữ liệu khoá. Vui lòng liên hệ quản trị viên.");
        return;
      }
      await MasterKeyManager.decryptAndLoad({
        encryptedMasterKey: status.encrypted_master_key,
        nonce: status.master_key_nonce,
        pbkdf2Salt: status.pbkdf2_salt,
        pbkdf2Iterations: status.pbkdf2_iterations,
        encryptedPrivateKey: status.encrypted_private_key,
        privateKeyNonce: status.private_key_nonce,
        password,
        userId,
      });
      setSessionPassword(password);
      const publicKeyBytes = MasterKeyManager.getPublicKeyBytes()!;
      setKeyState({ status: "ready", publicKeyBytes });
      onUnlocked();
    } catch {
      setError("Mật khẩu không đúng hoặc khoá bị hỏng. Vui lòng thử lại.");
    } finally {
      setLoading(false);
    }
  }

  function handleLogout() {
    MasterKeyManager.clear();
    logout();
  }

  return (
    // Overlay
    <div
      id="zkpa-unlock-modal"
      data-testid="zkpa-unlock-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="zkpa-unlock-title"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0, 0, 0, 0.65)",
      }}
    >
      {/* Card — explicit solid background */}
      <div
        style={{
          position: "relative",
          backgroundColor: "#ffffff",
          color: "#111827",
          borderRadius: "12px",
          boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
          width: "100%",
          maxWidth: "420px",
          margin: "0 16px",
          padding: "28px",
        }}
      >
        {/* Dismiss / Close Icon Button */}
        {onDismiss && (
          <button
            id="zkpa-unlock-close-btn"
            data-testid="zkpa-unlock-close"
            type="button"
            aria-label="Đóng mở khoá"
            onClick={onDismiss}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "transparent",
              border: "none",
              fontSize: "20px",
              color: "#9ca3af",
              cursor: "pointer",
              padding: "4px 8px",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        )}

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
          <SvgShieldCheck
            style={{ width: "26px", height: "26px", color: "#2563eb", flexShrink: 0 }}
            aria-hidden="true"
          />
          <h2
            id="zkpa-unlock-title"
            style={{ fontSize: "20px", fontWeight: 700, margin: 0, color: "#111827" }}
          >
            Mở khoá dữ liệu
          </h2>
        </div>
        <p style={{ fontSize: "13px", color: "#6b7280", marginBottom: "24px", marginTop: "4px" }}>
          Nhập mật khẩu đăng nhập để giải mã khoá bảo mật của bạn.
        </p>

        {/* Password form */}
        <form onSubmit={handleUnlock} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label
              htmlFor="zkpa-unlock-password"
              style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px", color: "#374151" }}
            >
              Mật khẩu
            </label>
            <input
              id="zkpa-unlock-password"
              data-testid="zkpa-unlock-password"
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(null); }}
              style={{
                width: "100%",
                border: error ? "1px solid #ef4444" : "1px solid #d1d5db",
                borderRadius: "8px",
                padding: "10px 14px",
                fontSize: "14px",
                backgroundColor: "#f9fafb",
                color: "#111827",
                outline: "none",
                boxSizing: "border-box",
              }}
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              style={{
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "8px 12px",
                fontSize: "13px",
                color: "#dc2626",
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {/* Primary: Mở khoá */}
          <button
            id="zkpa-unlock-submit-btn"
            data-testid="zkpa-unlock-submit"
            type="submit"
            onClick={handleUnlock}
            disabled={loading || !password}
            style={{
              width: "100%",
              padding: "11px",
              background: loading || !password ? "#c7d2fe" : "#2563eb",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              fontSize: "15px",
              fontWeight: 600,
              cursor: loading || !password ? "not-allowed" : "pointer",
              transition: "background 0.15s",
            }}
          >
            {loading ? "Đang mở khoá..." : "🔓 Mở khoá"}
          </button>
        </form>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "20px 0" }}>
          <div style={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
          <span style={{ fontSize: "12px", color: "#9ca3af", whiteSpace: "nowrap" }}>hoặc</span>
          <div style={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
        </div>

        {/* Secondary: Recovery + Dismiss + Logout */}
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            id="zkpa-recovery-btn"
            type="button"
            onClick={() => setShowRecovery(true)}
            style={{
              flex: 1,
              padding: "10px 8px",
              background: "#eff6ff",
              color: "#2563eb",
              border: "1px solid #93c5fd",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "background 0.15s",
            }}
          >
            🔑 Recovery
          </button>
          {onDismiss && (
            <button
              id="zkpa-unlock-dismiss-btn"
              data-testid="zkpa-unlock-dismiss"
              type="button"
              onClick={onDismiss}
              style={{
                flex: 1,
                padding: "10px 8px",
                background: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              ⏭ Bỏ qua
            </button>
          )}
          <button
            id="zkpa-logout-btn"
            type="button"
            onClick={handleLogout}
            style={{
              flex: 1,
              padding: "10px 8px",
              background: "#f9fafb",
              color: "#6b7280",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            ↩ Đăng xuất
          </button>
        </div>

        <p style={{ fontSize: "12px", color: "#9ca3af", textAlign: "center", marginTop: "14px" }}>
          Quên mật khẩu? Dùng <strong style={{ color: "#6b7280" }}>Recovery</strong> với 24 từ bí mật.
        </p>
      </div>
    </div>
  );
}
